import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_UMS_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please sign in again to continue.';

type UnauthorizedHandler = (reason: string) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;
let unauthorizedHandlerFiring = false;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

export function triggerSessionExpired(reason: string = SESSION_EXPIRED_MESSAGE) {
  if (!unauthorizedHandler || unauthorizedHandlerFiring) return;
  unauthorizedHandlerFiring = true;
  try {
    unauthorizedHandler(reason);
  } finally {
    // Reset on next tick so multiple in-flight 401s don't each fire a logout,
    // but a future genuinely-new auth failure still can.
    setTimeout(() => { unauthorizedHandlerFiring = false; }, 0);
  }
}

/**
 * Read the current idToken from the Cognito session.
 *
 * By default this uses Amplify's in-memory cached session and only round-trips
 * Cognito (idp + identity-pool) when the token is near expiry — so it does NOT
 * fire a refresh on every call. Pass forceRefresh=true only when you
 * deliberately want to re-mint the token (e.g. probing for a dead session or
 * recovering from a 401).
 */
/** The idToken we already have, and when it stops being usable. */
let cachedToken: { value: string; expiresAt: number } | null = null;
/** A fetch already running, so concurrent requests share it instead of each starting one. */
let tokenInFlight: Promise<string> | null = null;
/** Re-mint this far before the token actually expires, so a request never carries one that
 *  dies in transit. */
const EXPIRY_SKEW_MS = 2 * 60 * 1000;

/** A JWT's exp claim, in epoch ms. Falls back to a short life if it cannot be read, so an
 *  unparseable token is re-fetched soon rather than cached forever. */
function expiryOf(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload?.exp === "number" ? payload.exp * 1000 : Date.now() + 60_000;
  } catch {
    return Date.now() + 60_000;
  }
}

/** Forget the cached token. Call on logout: the next user must not inherit this one. */
export function clearAuthTokenCache() {
  cachedToken = null;
  tokenInFlight = null;
}

export async function getAuthToken(forceRefresh = false) {
  // Amplify caches the SESSION, but fetchAuthSession still resolves identity-pool
  // credentials, and concurrent callers each run their own exchange. One page load fired
  // ten cognito-identity round trips -- GetId + GetCredentialsForIdentity, five times over --
  // because every request went through this interceptor at once.
  //
  // So the token itself is held here until it is nearly expired, and callers arriving while a
  // fetch is in flight wait on that one rather than starting another.
  if (!forceRefresh) {
    if (cachedToken && Date.now() < cachedToken.expiresAt - EXPIRY_SKEW_MS) {
      return cachedToken.value;
    }
    if (tokenInFlight) return tokenInFlight;
  }

  const fetching = (async () => {
    const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error("No idToken available");
    cachedToken = { value: token, expiresAt: expiryOf(token) };
    return token;
  })();

  // A forced refresh is deliberately not shared: its whole purpose is to go and get a new
  // one, so joining an in-flight fetch of the old session would defeat it.
  if (!forceRefresh) tokenInFlight = fetching;
  try {
    return await fetching;
  } finally {
    if (tokenInFlight === fetching) tokenInFlight = null;
  }
}

/**
 * Force-refresh variant, kept for callers that specifically need a freshly
 * minted token (the 401 retry below and the visibility-change session probe).
 */
export async function getRawAuthToken() {
  return getAuthToken(true);
}

// REQUEST INTERCEPTOR: Automatically injects the JWT before every call.
// Uses the cached session (no per-request Cognito refresh).
apiClient.interceptors.request.use(async (config) => {
  try {
    // Attempt to get the token (cached; Amplify refreshes only near expiry)
    const token = await getAuthToken();

    if (token) {
      config.headers.Authorization = token;
    }
  } catch (error) {
    // DO NOT REJECT. Just log it and let the request proceed.
    // If the endpoint is public (Auth: NONE), it will succeed.
    // If the endpoint is private, the server will return a 401 anyway.
    console.warn("Guest session: Proceeding without Authorization header.");
  }

  return config; // Always return config to let the request happen
});


// RESPONSE INTERCEPTOR: on 401, force-refresh the Cognito session once and retry.
// If the retry still 401s (or the refresh itself fails), trigger the global
// session-expired handler so the user is logged out with a clear message
// rather than being left in a half-authenticated state.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retriedAfterAuthRefresh) {
      original._retriedAfterAuthRefresh = true;
      try {
        const token = await getRawAuthToken();
        original.headers = original.headers || {};
        original.headers.Authorization = token;
        return apiClient(original);
      } catch (refreshError) {
        console.warn("Token refresh after 401 failed; logging user out.", refreshError);
        triggerSessionExpired();
        return Promise.reject(error);
      }
    }

    // Retry already happened and we're still unauthorized — session is dead.
    if (error.response?.status === 401 && original?._retriedAfterAuthRefresh) {
      triggerSessionExpired();
    }

    return Promise.reject(error);
  }
);

export default apiClient;